import {
  Line2D as Line2DInterface,
  Point2D,
  Segment2D as LineSegment2DInterface,
  WorkLineScope,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, ModifierKeys } from '@paragon/utilities';

import {
  buildWorkLineFromGeometry,
  classifyClickDuringWorkLineInsertion,
  inferInsertionWorkLine,
  InsertionWorkLine,
  normalizePointSnapForWorkLineInsertion,
  tryActualizeWorkLines,
  WorkLineSnap,
} from '../../../library/layout-geometry/work-lines';
import { findNextName } from '../../../library/utilities/findNextName';
import { isInteractiveEditActive } from '../../selectors/interactiveEditingSelectors';
import {
  getDefaultWorkLineScope,
  getLevelGuidsAffectedByWorkLineEdit,
  getVisibleWorkLineGuids,
} from '../../selectors/layoutDataSelectors';
import {
  __UNSAFE_setWorkLineGestureLineType,
  applicationOnInsertingWorkLine,
  resetWorkLineGesture,
  setInsertionWorkLine,
  setWorkLineInsertionFirstSelection,
} from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { setRenderModeAndEnsureSelectedItemIsAppropriate } from '../cameraActions';
import { deleteItems } from '../deletionActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import { beginPickedPointMovement, PICKED_POINT_MOVEMENT_MODIFIER } from './layoutPickingActions';

export const startInsertWorkLine = (): ThunkActionVoid => (dispatch, getState) => {
  dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout', { force: true }));
  dispatch(applicationOnInsertingWorkLine({ scope: getDefaultWorkLineScope(getState()) }));
};

export const setHoveredWorkLineIfApplicable =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    const hoveredGuids = state.layout.hoveredWorkLineGuids;
    if (hoveredGuids.length !== 1 || hoveredGuids[0] !== guid) {
      dispatch(layoutSlice.actions.hoveredWorkLinesSet([guid]));
    }
  };

export const clearHoveredWorkLineIfActive =
  (guid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    if (state.layout.hoveredWorkLineGuids.includes(guid)) {
      dispatch(layoutSlice.actions.hoveredWorkLinesSet([]));
    }
  };

export const updateWorkLineGestureLineType =
  (lineType: InsertionWorkLine['type'] | null): ThunkActionVoid =>
  (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;

    if (insertingObjectInfo?.type === 'workLine' && insertingObjectInfo.liveGestureLineType !== lineType)
      dispatch(__UNSAFE_setWorkLineGestureLineType(lineType));
  };

export const insertReviewedWorkLine = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { insertingObjectInfo } = getState().application;
  if (insertingObjectInfo?.type !== 'workLine' || insertingObjectInfo.draft.phase !== 'reviewing') return;

  const geometries = tryActualizeWorkLines(insertingObjectInfo.draft.line);
  if (geometries.length === 0) return;

  await dispatch(finishWorkLineInsertionFromGeometries(geometries.map((geometry) => geometry.toObject())));
};

export const handleSceneClickDuringWorkLineInsertion =
  (snap: WorkLineSnap, options: { clientPosition: Point2D; modifiers: ModifierKeys }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'workLine') return;

    switch (insertingObjectInfo.draft.phase) {
      case 'idle': {
        const selection = classifyClickDuringWorkLineInsertion(snap);

        // only a first point can be moved; there's no "other point" yet, so the modifier has no other meaning here
        if (selection.type === 'point' && options.modifiers[PICKED_POINT_MOVEMENT_MODIFIER]) {
          const pointSnap = normalizePointSnapForWorkLineInsertion(snap.pointSnap, snap.unsnappedPoint);
          dispatch(beginPickedPointMovement({ type: 'workLineFirstSelection' }, pointSnap, options));
          break;
        }

        dispatch(setWorkLineInsertionFirstSelection(selection));
        break;
      }

      case 'gesturing': {
        await dispatch(concludeWorkLineGesture(snap, options.modifiers.altKey));
        break;
      }

      case 'reviewing':
        return;

      default:
        assertExhaustiveSwitch(insertingObjectInfo.draft);
    }
  };

const concludeWorkLineGesture =
  (snap: WorkLineSnap, forceAutoInsert = false): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo, snappingLevel } = getState().application;
    if (insertingObjectInfo?.type !== 'workLine' || insertingObjectInfo.draft.phase !== 'gesturing') return;

    const insertionLine = inferInsertionWorkLine(
      insertingObjectInfo.draft.firstSelection,
      snap,
      insertingObjectInfo.settings,
      snappingLevel,
    );
    if (insertionLine == null) return;

    if (insertingObjectInfo.settings.autoInsert || forceAutoInsert) {
      const geometries = tryActualizeWorkLines(insertionLine);
      if (geometries.length === 0) return;

      await dispatch(finishWorkLineInsertionFromGeometries(geometries.map((geometry) => geometry.toObject())));
    } else {
      dispatch(setInsertionWorkLine(insertionLine));
    }
  };

export const finishWorkLineInsertionFromGeometries =
  (geometries: (Line2DInterface | LineSegment2DInterface)[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const { insertingObjectInfo } = state.application;

    if (insertingObjectInfo?.type !== 'workLine' || geometries.length === 0) return;

    const takenNames = Object.values(state.layout.workLines).map((line) => line.name);

    const lines = geometries.map((geometry) => {
      const name = findNextName(takenNames, { prefix: 'WL' });
      takenNames.push(name);

      return buildWorkLineFromGeometry(geometry, {
        guid: crypto.randomUUID(),
        name,
        bounding: insertingObjectInfo.settings.bounding,
        scope: insertingObjectInfo.scope,
      });
    });

    const affectedLevelGuids = getLevelGuidsAffectedByWorkLineEdit(state, [], [insertingObjectInfo.scope]);
    dispatch(layoutSlice.actions.workLinesUpserted({ workLines: lines, affectedLevelGuids }));
    dispatch(resetWorkLineGesture());

    await dispatch(saveWorkLines());
  };

export const toggleWorkLineBoundingStatus =
  (guids: string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const affectedLevelGuids = getLevelGuidsAffectedByWorkLineEdit(getState(), guids, []);
    dispatch(layoutSlice.actions.workLinesBoundingToggled({ guids, affectedLevelGuids }));
    await dispatch(saveWorkLines());
  };

export const setWorkLineScope =
  (guids: readonly string[], scope: WorkLineScope): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const affectedLevelGuids = getLevelGuidsAffectedByWorkLineEdit(getState(), guids, [scope]);
    dispatch(layoutSlice.actions.workLinesScopeChanged({ guids, scope, affectedLevelGuids }));
    await dispatch(saveWorkLines());
  };

export const saveWorkLines = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return;

  const { workLines } = state.layout;
  const { projectGuid } = state.application.openContext;

  const spinnerId = dispatch(addSpinnerMessage('Saving work lines...'));

  try {
    await designService.projects.replaceWorkLines(projectGuid, workLines);
  } catch (error) {
    console.error(error);
    dispatch(enqueueError('Unable to save work lines. Please try again or contact Paragon Customer Support.'));
    return;
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const insertWorkLinesFromDxf =
  (segments: readonly LineSegment2DInterface[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const takenNames = Object.values(state.layout.workLines).map((line) => line.name);

    const workLines = segments.map((segment) => {
      const name = findNextName(takenNames, { prefix: 'Import' });
      takenNames.push(name);

      return {
        guid: crypto.randomUUID(),
        name,
        bounding: false,
        scope: { type: 'Project' as const },
        geometry: segment,
        geometryType: 'Segment' as const,
      };
    });

    dispatch(layoutSlice.actions.workLinesUpserted({ workLines, affectedLevelGuids: null }));
    await dispatch(saveWorkLines());
  };

export const deleteAllWorkLines = (): ThunkActionVoid => (dispatch, getState) => {
  const workLineGuids = getVisibleWorkLineGuids(getState());
  const workLineItems = [...workLineGuids].map((guid) => ({ type: 'workLine' as const, guid }));

  dispatch(deleteItems(workLineItems));
};
